import {
  Alert,
  Col,
  Row,
} from "reactstrap";
import Loading from "../../components/Loading";
import { TaskAssignment } from "../../interfaces/models/TaskAssignment";
import useDashboardTasks from "../../hooks/useDashboardTasks";
import { TaskSequence, Sequence } from "../../interfaces/models/TaskSequence";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DESK_COACH,
  WORKHEALTH_COACH,
} from "../../components/Routes/routingConstants";
import { User } from "../../interfaces/models/User"; 
import WeeklyChallengeCard from "../../components/Dashboard/WeeklyChallengeCard";
import HealthHubCarousel from "../../components/Dashboard/HealthHubCarousel";
import TaskSequenceCarousel from "../../components/Dashboard/TaskSequenceCarousel";
import MovementCoachPreview from "../../components/Dashboard/MovementCoachPreview";
import { useUserSettingsWithOrganisation } from "../../hooks/useUserDetails";
import { UserSettings } from "../../interfaces/hookModels/UserSettings";

import moment from "moment";

const DashboardPage = () => {
  const [dashboardView, setDashboardView] = useState<string>(null);
  const [dashboardTaskAssignments, setDashboardTaskAssignments] = useState<TaskAssignment[]>([]);
  const [dashboardTaskSequence, setDashboardTaskSequence] =
    useState<TaskSequence>(null);

  const settingsRef = useRef<UserSettings | null>(null);

  const { data: organisationData } = useUserSettingsWithOrganisation();
  const { data, loading, error } = useDashboardTasks();

  const products = data?.me.user_license?.products
    ?.split(",")
    .map((product: string) => {
      return product.trim();
    });
  
  const firstProduct = products?.[0] || null;

  const AddMinuteInterval = useCallback((date : Date, maxLength : number, index : number) => {
    const dateNow = new Date();
    const nudgeDelay = settingsRef.current.nudge_delay;
    let nextAssignedAt = moment(date).add(index * nudgeDelay, 'minutes').toDate();

    if(maxLength - 1 === index) {
      let i = maxLength -1 ;
      let computedAssignedDate = moment(date).add(i * nudgeDelay, 'minutes').toDate();
      while(computedAssignedDate < dateNow) {
        computedAssignedDate = moment(date).add(i * nudgeDelay, 'minutes').toDate();
        i++;
      }

      nextAssignedAt = computedAssignedDate;
    }

    return nextAssignedAt;
  },[]);

  const nudgeIntervalRefresh = useCallback((isFirst : boolean, task : TaskAssignment, sequences : Sequence[], scheduledTaskAssignment : TaskAssignment[]) => {
    const dateNow = new Date();
    const assignedAt = new Date(task.assigned_at);
    let difference : number = (assignedAt.getTime() - dateNow.getTime());
    const currentIndex = scheduledTaskAssignment.length - 1;
    let cloneTaskAssignments = [...scheduledTaskAssignment];
 
    let nextTaskSequence : Sequence | null = null;
    let nextTask : TaskAssignment | null = null;
    if(sequences.length === currentIndex + 1) {
      nextTaskSequence = sequences[sequences.length - 1];
    }
    else {
      nextTaskSequence = sequences[currentIndex + 1];
    }

    let nextAssignedAt = AddMinuteInterval(task.assigned_at, sequences.length, 1);
    if(!isFirst) {

      if(cloneTaskAssignments.length !== sequences.length) {
        cloneTaskAssignments[ cloneTaskAssignments.length - 1].user_acknowledged = true;
        cloneTaskAssignments[ cloneTaskAssignments.length - 1].task_complete = true;

        nextTask = {
          _id: nextTaskSequence.task._id,
          assigned_at: nextAssignedAt,
          user_acknowledged: false,
          user_acknowledged_at: null,
          task_complete: false,
          task: nextTaskSequence.task,
        };
        
        cloneTaskAssignments.push(nextTask);
      }
      else {
        cloneTaskAssignments[ cloneTaskAssignments.length - 1].assigned_at = nextAssignedAt;
        nextTask = task;
      }
      
      setDashboardTaskAssignments(cloneTaskAssignments);
      difference  = (nextAssignedAt.getTime() - dateNow.getTime());

    } 
    else {
      nextTask = task;
    }

    setTimeout(() => {

      nudgeIntervalRefresh(false ,nextTask, sequences, cloneTaskAssignments);

    },difference);

  },[AddMinuteInterval]);


  useEffect(() => {
    if (data && organisationData) {

      const dateNow = new Date();
      const orgData : User = organisationData.me;
      let nudgeInteractionStart : Date | null = null;
      let settingsHolder : UserSettings = { nudge_delay :0, nudge_interaction : false};

      if(orgData.organisation) {

        if(orgData.organisation.allow_user_customisation === true) { 
          settingsHolder.nudge_delay = orgData.nudge_delay;
          settingsHolder.nudge_interaction = orgData.nudge_interaction;
          nudgeInteractionStart = orgData.nudge_no_interaction_start;
        }
        else {
          settingsHolder.nudge_delay = orgData.organisation.nudge_delay;
          settingsHolder.nudge_interaction = orgData.organisation.nudge_interaction;
          nudgeInteractionStart = orgData.organisation.nudge_no_interaction_start;
        }
        if(settingsHolder.nudge_interaction === false &&  moment(dateNow).startOf('day').toDate() < moment(nudgeInteractionStart).startOf('day').toDate()){
          settingsHolder.nudge_interaction = true;
        }
        settingsRef.current = settingsHolder;
      }
      // Set dashboard view
      if (dashboardView === WORKHEALTH_COACH) {
        let taskAssignments: TaskAssignment[] = [];

        if (data.workhealth_task_assignments !== null) {
          taskAssignments = data.workhealth_task_assignments.map((wTA: any) => {
            return { ...wTA, task: wTA.workhealth_task };
          });
        }
        setDashboardTaskAssignments(taskAssignments);

        if (data.me.workhealth_sequence !== null) {
          // Filter out any null tasks in Sequence
          const filteredWHTaskSequence =
            data.me.workhealth_sequence.sequence.filter((WSeq: any) => {
              return WSeq.workhealth_task != null;
            });
          const whTaskSequence = filteredWHTaskSequence.map((WSeq: any) => {
            return { ...WSeq, task: { ...WSeq.workhealth_task } };
          });
          const dashboardTaskSequence = {
            _id: data.me.workhealth_sequence._id,
            sequence: whTaskSequence,
          };
          setDashboardTaskSequence(dashboardTaskSequence);
        }
      } else if (dashboardView === DESK_COACH) {
        if(settingsHolder?.nudge_interaction === true){
          setDashboardTaskAssignments(data.task_assignments);
        }
        else {
          let taskAssignmentsNoInteraction: TaskAssignment[] = [];
          if(data.task_assignments.length !== 0) {
            let firstTaskAssignment : TaskAssignment = data.task_assignments[0];
            const taskSequences = data.me.task_sequence.sequence;
            
            const sequenceCount = taskSequences.length;
            for(let x = 0; x < sequenceCount; x++) {
              let scheduled_task = taskSequences[x];
              // let nextAssignedAt = moment(firstTaskAssignment.assigned_at).add(x * orgData.nudge_delay, 'minutes').toDate();

              // if(sequenceCount - 1 === x) {
              //   let i = sequenceCount -1 ;
              //   let computedAssignedDate = moment(firstTaskAssignment.assigned_at).add(i * orgData.nudge_delay, 'minutes').toDate();
              //   while(computedAssignedDate < dateNow) {
              //     computedAssignedDate = moment(firstTaskAssignment.assigned_at).add(i * orgData.nudge_delay, 'minutes').toDate();
              //     i++;
              //   }

              //   nextAssignedAt = computedAssignedDate;
              // }
              let nextAssignedAt = AddMinuteInterval(firstTaskAssignment.assigned_at, sequenceCount, x);
              taskAssignmentsNoInteraction.push({
                _id: scheduled_task.task._id,
                assigned_at: nextAssignedAt,
                user_acknowledged: nextAssignedAt < dateNow,
                user_acknowledged_at: null,
                task_complete: nextAssignedAt < dateNow,
                task: scheduled_task.task,
              });

              if(nextAssignedAt > dateNow){
                break;
              }
            }
            nudgeIntervalRefresh(true, taskAssignmentsNoInteraction[taskAssignmentsNoInteraction.length - 1], taskSequences, taskAssignmentsNoInteraction);
            setDashboardTaskAssignments(taskAssignmentsNoInteraction);
          }
          
        }
        
        if (data.me.task_sequence != null) {
          // Filter out null any null tasks in Sequence
          const filteredTaskSequence = data.me.task_sequence.sequence.filter(
            (s) => {
              return s.task != null;
            }
          );
          const dashboardTaskSequence = {
            _id: data.me.task_sequence._id,
            sequence: filteredTaskSequence,
          };
          setDashboardTaskSequence(dashboardTaskSequence);
        }
      } else if (data.me.user_license?.products?.length > 0) {
        setDashboardView(firstProduct);
      }
    }
  }, [data, organisationData, dashboardView, firstProduct, nudgeIntervalRefresh, AddMinuteInterval]);

  const userIsLicensed: boolean = products?.length > 0 ? true : false;


  return (
    <>
      {loading && <Loading />}
      {error && (
        <Alert color="danger">
          Oops, we’re having trouble refreshing this connection. Please give it a minute, and if it doesn’t resolve, let us know.
        </Alert>
      )}
      {(data && userIsLicensed) && (
        <Col>
          <Row>
            <Col md={12}>
              <h3 >Your mission, should you choose to accept it?</h3>
            </Col>
            <Col md={12} style={{minHeight:250}}>
              <WeeklyChallengeCard />
            </Col>
            <Col md={12} className="mt-1">
              <Row>
                <Col md={6}>
                  <h3 className=" mt-5">Upcoming Nudges</h3>
                  {
                    (dashboardTaskSequence && dashboardTaskAssignments) &&
                    <TaskSequenceCarousel
                      task_sequence={dashboardTaskSequence}
                      task_assignments={dashboardTaskAssignments}
                      settings={settingsRef.current}
                    />
                  }
                  
                </Col>
                <Col md={6}>
                  <h3 className="mt-5">Try Me!</h3>
                  <MovementCoachPreview />
                </Col>
              </Row>
            </Col>
            <Col md={12} className="mt-5">
              <h3 >Health Hub</h3>
            </Col>
            <Col md={12} className="mt-1" style={{minHeight:170}}>
              <HealthHubCarousel />
            </Col>
          </Row>
        </Col>
      )}
    </>
  );
};

export { DashboardPage as Dashboard };
